
import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { convertBooleanToStatus, convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import MenuConfig from "../../../../constants/MenuConfig";
import CatalogForm from "./CatalogForm";
import { getId, prepareUploadFileData, sendToUploadFileApi } from "../../../../utils/ApiUtils";
import { useNavigate } from "react-router-dom";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";

const CatalogNew = ({ type }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState(true);
    const [trackStock, setTrackStock] = useState(false);
    const [selectAllBranches, setSelectAllBranches] = useState(false);
    const [entityData, setEntityData] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const navigate = useNavigate();
    const isService = type === "SERVICE";
    const menu = isService ? MenuConfig.main.service : MenuConfig.main.product;
    const entityTitle = isService ? t('service') : t('product');

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        name: Yup.string()
            .min(3, t("validation.min.generic", { field: t('name'), qty: 3 }))
            .required(t('validation.required.name')),
        price: Yup.string()
            .required(t('validation.required.price')),
        detail: Yup.string().optional(),
        category: Yup.object()
            .required(t('validation.required.category'))
            .nullable(),
        vendor: Yup.object().nullable().optional(),
        icms: Yup.string().optional(),
        barCode: Yup.string().nullable().optional(),
        refCode: Yup.string().nullable().optional(),
        branches: Yup.array()
            .required(t('validation.required.branch'))
            .min(1, t('validation.required.branch')),
        costPrice: Yup.string().optional(),
        otherTaxes: Yup.string().optional(),
        extraCosts: Yup.string().optional(),
        profitMargin: Yup.string().optional(),
        suggestedPrice: Yup.string().optional(),
        // Obrigatório para serviço, mas aceitando zero: o campo precisa estar preenchido, e
        // zero é uma resposta válida ("não reserva janela na agenda"). Antes a agenda
        // chutava 30 minutos em silêncio quando o campo vinha vazio. Produto não ocupa
        // agenda e por isso segue sem o campo.
        duration: isService
            ? Yup.number()
                .typeError(t('message.enter_valid_number'))
                .integer(t('service_duration_integer_validation'))
                .min(0, t('message.must_not_be_negative'))
                .max(600, t('service_duration_max_validation'))
                .required(t('validation.required.service_duration'))
            : Yup.number()
                .typeError(t('message.enter_valid_number'))
                .integer(t('service_duration_integer_validation'))
                .min(0, t('message.must_not_be_negative'))
                .max(600, t('service_duration_max_validation'))
                .nullable()
                .optional(),
        depositValue: Yup.string().when('requiresDeposit', {
            is: true,
            then: schema => schema.test('deposit-value', t('deposit_value_required'), value => {
                const number = Number(convertStringToStringDouble(value));
                return !isNaN(number) && number > 0;
            }).test('deposit-percent-max', t('deposit_percent_max'), function (value) {
                // Percentual acima de 100 cobraria mais do que o próprio serviço.
                if (this.parent.depositType !== 'PERCENT') {
                    return true;
                }
                return Number(convertStringToStringDouble(value)) <= 100;
            }),
            otherwise: schema => schema.optional()
        }),
        repurchaseIntervalDays: Yup.number()
            .typeError(t('message.enter_valid_number'))
            .integer(t('repurchase_interval_integer_validation'))
            .min(1, t('message.must_be_greater_than_zero'))
            .max(3650, t('repurchase_interval_max_validation'))
            .nullable()
            .optional(),
    });

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            name: '',
            price: '0,00',
            // Preço fechado é o padrão; "a partir de" é a exceção do serviço que fecha valor
            // com o cliente na cadeira.
            priceFrom: false,
            // Aparecer no site é o padrão; esconder é a exceção do serviço de uso interno.
            showInPublicBooking: true,
            // Serviço sem reserva é o padrão; a exigência é a exceção do serviço caro que prende
            // o tempo de um colaborador.
            requiresDeposit: false,
            depositType: 'AMOUNT',
            depositValue: '0,00',
            detail: '',
            category: null,
            vendor: null,
            icms: '0,00',
            costPrice: '0,00',
            otherTaxes: '0,00',
            extraCosts: '0,00',
            profitMargin: '30,00',
            suggestedPrice: '0,00',
            barCode: '',
            refCode: '',
            repurchaseIntervalDays: '',
            // Serviço nasce com zero: o sistema não inventa uma janela na agenda, e quem
            // cadastra informa o tempo real. Produto não usa o campo.
            duration: isService ? 0 : ''
        },
        validationSchema: formSchema,
        onSubmit: values => {
            // Categoria do item
            let category = null;
            if (values.category) {
                category = {
                    id: values.category.value
                };
            }

            let branches = [];
            values.branches.forEach((item) => {
                branches.push({
                    id: item.value
                });
            });

            let vendor = null;
            if (values.vendor !== null) {
                vendor = {
                    id: values.vendor.value
                }
            }

            // Regras
            let entity = {
                id: getId(entityData),
                type: type,
                name: values.name,
                price: convertStringToStringDouble(values.price),
                priceFrom: values.priceFrom === true,
                showInPublicBooking: values.showInPublicBooking !== false,
                // Tipo e valor só seguem quando a reserva é exigida; o servidor também os zera.
                requiresDeposit: isService && values.requiresDeposit === true,
                depositType: isService && values.requiresDeposit === true ? values.depositType : null,
                depositValue: isService && values.requiresDeposit === true
                    ? convertStringToStringDouble(values.depositValue) : null,
                detail: values.detail,
                category: category,
                vendor: vendor,
                status: convertBooleanToStatus(status),
                barCode: values.barCode,
                branches: branches,
                trackStock: trackStock,
                icms: !values.icms ? 0 : convertStringToStringDouble(values.icms),
                costPrice: !values.costPrice ? 0 : convertStringToStringDouble(values.costPrice),
                otherTaxes: !values.otherTaxes ? 0 : convertStringToStringDouble(values.otherTaxes),
                extraCosts: !values.extraCosts ? 0 : convertStringToStringDouble(values.extraCosts),
                profitMargin: !values.profitMargin ? 0 : convertStringToStringDouble(values.profitMargin),
                suggestedPrice: !values.suggestedPrice ? 0 : convertStringToStringDouble(values.suggestedPrice),
                unit: 'UNIT', // Único valor em uso por enquanto; KG, G e L ficam para depois.
                refCode: values.refCode,
                // Vazio desabilita o ciclo; um número permite calcular a próxima oportunidade.
                repurchaseIntervalDays: values.repurchaseIntervalDays || null,
                // Zero é valor, não vazio: `|| null` transformaria o zero informado em nulo
                // e a agenda voltaria a aplicar o padrão. No produto o campo vai nulo,
                // porque produto não ocupa janela na agenda.
                duration: isService ? Number(values.duration ?? 0) : (values.duration || null),
            }

            // Prepara os dados.
            prepareUploadFileData(uploadedFile, entity, "main/products",
                entityData).then(data => {
                    // Aciona a api
                    sendToUploadFileApi(Api.main.product + "/upload", data, entity.id, () => {
                        if (entityData == null) {
                            onReset();
                        } else {
                            navigate(menu.route);
                        }
                    }, (error) => {
                        ApiError(error);
                        formik.setSubmitting(false);
                    });
                }).catch((error) => {
                    ApiError(error);
                    formik.setSubmitting(false);
                });
        },
    });

    // Valida o form e em seguida submete os dados. Aciona o onSubmit do formik.
    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    const onReset = () => {
        formik.resetForm();

        // Força os campos customizados.
        setStatus(true);
        setTrackStock(false);
        setSelectAllBranches(false);
        setUploadedFile(null);
    }

    useEffect(() => {
        if (entityData?.type && entityData.type !== type) {
            const targetMenu = entityData.type === "SERVICE"
                ? MenuConfig.main.service
                : MenuConfig.main.product;
            // Evita que uma URL do catálogo incorreto altere o tipo do registro ao salvar.
            navigate(`${targetMenu.route}/new/${entityData.id}`, { replace: true });
        }
    }, [entityData, navigate, type]);

    return (
        <>
            <NewPage
                motherMenu={t('main')}
                childMenu={entityTitle}
                activeMenu={t('new')}
                child={
                    <CatalogForm
                        formik={formik}
                        status={status}
                        setStatus={setStatus}
                        selectAllBranches={selectAllBranches}
                        setSelectAllBranches={setSelectAllBranches}
                        entityData={entityData}
                        setUploadedFile={setUploadedFile}
                        uploadedFile={uploadedFile}
                        setTrackStock={setTrackStock}
                        trackStock={trackStock}
                        isService={isService}
                    />
                }
                setEntityData={setEntityData}
                path={Api.main.product}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={menu.route}
            />
        </>
    );
}

export default CatalogNew;
